import { useEffect } from 'react';

/**
 * A bottom sheet — platform UI, shared by any module's editors (the agenda's
 * item editors and the todo editor both use it). A backdrop that closes on tap,
 * a panel anchored to the bottom where a thumb is, and a grip that doubles as the
 * close button. Escape closes it too, for the keyboard.
 *
 * It lives in `platform/` rather than in a module so that a module borrowing the
 * shell never imports another module's internals (the independence rule) — the
 * component carries no module logic, only the shell.
 */
export function Sheet({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <>
      <div className="sheet-backdrop" onClick={onClose} />
      <div className="sheet" role="dialog" aria-modal="true" aria-label={title}>
        <button className="sheet-grip" aria-label="Sluiten" onClick={onClose} />
        <h2>{title}</h2>
        {children}
      </div>
    </>
  );
}
